import { Group } from "@fairfleet/geotab";
import { useMemo } from "react";
import {
  asGroupArray,
  getAssignableClearanceIds,
  isArchived,
} from "../usersPageHelpers";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { COMPANY_GROUP_ID, ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";
import { RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import { useGroups } from "@/hooks/groups/useGroups";
import { useSecurityClearances } from "@/hooks/groups/useSecurityClearances";
import { useDebounced } from "@/hooks/useDebounced";
import { useUserById } from "@/hooks/users/useUserById";
import { useUsersBySearch } from "@/hooks/users/useUsersBySearch";
import { buildGroupIndex, isUnderBranch } from "@/utilities/groupIndex";
import { normalizeSearchText } from "@/utilities/text/normalizeSearchText";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

export type UserStatusFilter = "all" | "active" | "archived";
export type UserTab = "users" | "drivers";

interface Params {
  search: string;
  tab: UserTab;
  statusFilter: UserStatusFilter;
  selectedUserId: string | null;
  isCreating: boolean;
}

/** All server data of the users page plus the group-tree derivations built from it. */
export function useUsersPageData({
  search,
  tab,
  statusFilter,
  selectedUserId,
  isCreating,
}: Params) {
  const { me, meResolved } = useGeotabClient();
  const { data: groups = [], isError: groupsError } = useGroups();
  const { data: securityBranchGroups = [], isError: securityBranchError } =
    useSecurityClearances();

  const debouncedSearch = useDebounced(search, 300);
  const {
    data: users = [],
    isLoading,
    isFetching: isFetchingUsers,
    isError: usersError,
  } = useUsersBySearch(
    debouncedSearch,
    tab === "drivers",
    statusFilter !== "active",
    false,
    true,
    RESULTS_LIMIT_USER,
  );
  const { data: selectedUserById = null, isError: selectedUserError } = useUserById(
    isCreating ? undefined : (selectedUserId ?? undefined),
  );

  const securityClearances = useMemo(() => asGroupArray(securityBranchGroups), [securityBranchGroups]);

  const allGroups = useMemo(() => {
    const byId = new Map<string, Group>();
    for (const group of asGroupArray(groups)) byId.set(group.id, group);
    for (const group of securityClearances) byId.set(group.id, group);
    return [...byId.values()];
  }, [groups, securityClearances]);

  const groupIndex = useMemo(() => buildGroupIndex(allGroups), [allGroups]);
  const groupById = groupIndex.byId;

  const securityGroupsSorted = useMemo(() => {
    const source = securityClearances.length > 0 ? securityClearances : allGroups;
    return source
      .filter(
        group =>
          isUnderBranch(groupIndex, group.id, ROOT_SECURITY_GROUP_ID) ||
          normalizeSearchText(group.name).includes("security") ||
          group.id.endsWith("SecurityId"),
      )
      .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
  }, [allGroups, groupIndex, securityClearances]);

  const companyGroupOptions = useMemo(
    () =>
      allGroups.filter(group => isUnderBranch(groupIndex, group.id, COMPANY_GROUP_ID)),
    [allGroups, groupIndex],
  );

  const assignableClearanceIds = useMemo(
    () => getAssignableClearanceIds(me, securityGroupsSorted, groupIndex),
    [me, securityGroupsSorted, groupIndex],
  );

  const sortedUsers = useMemo(() => {
    const visible = users.filter(user => {
      if (statusFilter === "all") return true;
      if (statusFilter === "archived") return isArchived(user);
      return !isArchived(user);
    });
    return visible.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
  }, [statusFilter, users]);

  const selectedUser = selectedUserById ?? users.find(user => user.id === selectedUserId) ?? null;

  const selectedUserSecurityGroups = useMemo(
    () => (selectedUserById?.securityGroups ?? []).map(group => groupById.get(group.id) ?? group),
    [groupById, selectedUserById],
  );

  return {
    me,
    /** True when the list filled a whole page, so more users may exist than are shown. */
    isUserListTruncated: users.length >= RESULTS_LIMIT_USER,
    // `meResolved` without a `me` means the signed-in user could not be found or loaded.
    loadError:
      groupsError || securityBranchError || selectedUserError || usersError || (meResolved && !me),
    groupsError,
    securityBranchError,
    usersError,
    isLoading,
    /** A search or refetch is running while the previous list is still shown. */
    isRefreshingUsers: isFetchingUsers && !isLoading,
    sortedUsers,
    /** The search the list was fetched with; keys the list so "Show more" resets per search, not per keystroke. */
    debouncedSearch,
    selectedUser,
    selectedUserById,
    selectedUserSecurityGroups,
    groupById,
    securityGroupsSorted,
    companyGroupOptions,
    assignableClearanceIds,
  };
}
